// src/admin/AdminProducts.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

import { db, storage } from "../services/firebase";

const CATEGORY_OPTIONS = [
  {
    label: "Imprimantes à ticket",
    categorySlug: "imprimantes-ticket",
    type: "ticket-printer",
  },
  {
    label: "Imprimantes classiques",
    categorySlug: "imprimantes-classiques",
    type: "classic-printer",
  },
  {
    label: "Rouleaux de papier",
    categorySlug: "rouleaux-papier",
    type: "paper-roll",
  },
  {
    label: "TPE",
    categorySlug: "tpe",
    type: "tpe",
  },
  {
    label: "EPOS / Caisses",
    categorySlug: "epos",
    type: "epos",
  },
  {
    label: "Scanners",
    categorySlug: "scanners",
    type: "scanner",
  },
  {
    label: "Caisses manuelles",
    categorySlug: "caisses-manuelles",
    type: "manual-cash",
  },
];

/**
 * Configuration des champs techniques par type.
 */
const SPEC_CONFIG = {
  "ticket-printer": [
    {
      name: "paperWidthMm",
      label: "Largeur papier (mm)",
      type: "number",
    },
    {
      name: "printingMethod",
      label: "Méthode d'impression",
      type: "text",
    },
    {
      name: "printSpeedMms",
      label: "Vitesse d'impression (mm/s)",
      type: "number",
    },
    {
      name: "interfaces",
      label: "Interfaces (USB, LAN…)",
      type: "text",
    },
    {
      name: "autoCutter",
      label: "Coupe automatique",
      type: "boolean",
    },
    {
      name: "cashDrawerPort",
      label: "Port tiroir caisse",
      type: "boolean",
    },
    {
      name: "resolutionDpi",
      label: "Résolution (dpi)",
      type: "text",
    },
    {
      name: "osSupport",
      label: "OS supportés",
      type: "text",
    },
    {
      name: "usage",
      label: "Usage recommandé",
      type: "text",
    },
  ],

  "classic-printer": [
    {
      name: "printerType",
      label: "Type (Laser, Jet…)",
      type: "text",
    },
    {
      name: "colorMode",
      label: "Mode (Couleur / N&B)",
      type: "text",
    },
    {
      name: "format",
      label: "Format (A4, A3…)",
      type: "text",
    },
    {
      name: "printSpeedPpm",
      label: "Vitesse (ppm)",
      type: "number",
    },
    {
      name: "resolutionDpi",
      label: "Résolution (dpi)",
      type: "text",
    },
    {
      name: "duplex",
      label: "Recto-verso auto",
      type: "boolean",
    },
    {
      name: "connectivity",
      label: "Connectivité",
      type: "text",
    },
    {
      name: "usage",
      label: "Usage recommandé",
      type: "text",
    },
  ],

  "paper-roll": [
    {
      name: "paperWidthMm",
      label: "Largeur papier (mm)",
      type: "number",
    },
    {
      name: "diameterMm",
      label: "Diamètre (mm)",
      type: "number",
    },

    {
      name: "lengthMm",
      label: "Longueur (mm)",
      type: "number",
    },

    {
      name: "coreSizeMm",
      label: "Mandrin / noyau (mm)",
      type: "number",
    },
    {
      name: "paperType",
      label: "Type de papier",
      type: "text",
    },
    {
      name: "compatibleDevices",
      label: "Appareils compatibles",
      type: "text",
    },
    {
      name: "rollsPerPack",
      label: "Rouleaux par pack",
      type: "number",
    },
  ],

  tpe: [
    {
      name: "tpeType",
      label: "Type de TPE (GPRS, Android…)",
      type: "text",
    },
    {
      name: "paymentNetworks",
      label: "Réseaux (Visa, Mobile Money…)",
      type: "text",
    },
    {
      name: "hasIntegratedPrinter",
      label: "Imprimante intégrée",
      type: "boolean",
    },
    {
      name: "screenSizeInches",
      label: "Taille écran (pouces)",
      type: "text",
    },
    {
      name: "memory",
      label: "Mémoire (RAM / ROM)",
      type: "text",
    },
    {
      name: "batteryMah",
      label: "Batterie (mAh)",
      type: "number",
    },
    {
      name: "connectivity",
      label: "Connectivité (4G, Wi-Fi…)",
      type: "text",
    },
    {
      name: "usage",
      label: "Usage recommandé",
      type: "text",
    },
  ],

  epos: [
    {
      name: "formFactor",
      label: "Format (All-in-one…)",
      type: "text",
    },
    {
      name: "cpu",
      label: "Processeur (CPU)",
      type: "text",
    },
    {
      name: "ram",
      label: "RAM",
      type: "text",
    },
    {
      name: "storage",
      label: "Stockage",
      type: "text",
    },
    {
      name: "os",
      label: "Système (Android, Windows…)",
      type: "text",
    },
    {
      name: "screenSizeInches",
      label: "Taille écran (pouces)",
      type: "text",
    },
    {
      name: "touch",
      label: "Écran tactile",
      type: "boolean",
    },
    {
      name: "ports",
      label: "Ports (USB, LAN…)",
      type: "text",
    },
    {
      name: "includedPeripherals",
      label: "Périphériques inclus",
      type: "text",
    },
  ],

  scanner: [
    {
      name: "scannerType",
      label: "Type de scanner (pistolet, plat…)",
      type: "text",
    },
    {
      name: "scanTechnology",
      label: "Technologie (Laser, CCD, Imager…)",
      type: "text",
    },
    {
      name: "scanSpeed",
      label: "Vitesse de scan (scans/s)",
      type: "number",
    },
    {
      name: "resolutionDpi",
      label: "Résolution (dpi)",
      type: "text",
    },
    {
      name: "interfaces",
      label: "Interfaces (USB, RS232, Bluetooth…)",
      type: "text",
    },
    {
      name: "supportedCodes",
      label: "Codes supportés (EAN13, QR, etc.)",
      type: "text",
    },
    {
      name: "wireless",
      label: "Sans fil (Bluetooth / RF)",
      type: "boolean",
    },
    {
      name: "usage",
      label: "Usage recommandé (caisse, stock…)",
      type: "text",
    },
  ],

  "manual-cash": [
    {
      name: "drawerSize",
      label: "Taille de la caisse (L x l x h)",
      type: "text",
    },
    {
      name: "compartmentsBills",
      label: "Compartiments billets",
      type: "number",
    },
    {
      name: "compartmentsCoins",
      label: "Compartiments pièces",
      type: "number",
    },
    {
      name: "lockType",
      label: "Type de serrure (clé, 3 positions…)",
      type: "text",
    },
    {
      name: "material",
      label: "Matériau (métal, acier…)",
      type: "text",
    },
    {
      name: "color",
      label: "Couleur",
      type: "text",
    },
    {
      name: "weightKg",
      label: "Poids (kg)",
      type: "number",
    },
    {
      name: "usage",
      label: "Usage recommandé (boutique, pharmacie…)",
      type: "text",
    },
  ],
};

const EMPTY_FORM = {
  name: "",
  slug: "",
  brand: "",
  categorySlug: CATEGORY_OPTIONS[0].categorySlug,
  type: CATEGORY_OPTIONS[0].type,
  price: "",
  oldPrice: "",
  inStock: true,
  stockQty: "",
  shortDescription: "",
  description: "",
  isFeatured: false,
};

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Nettoie et convertit les spécifications avant Firestore.
 *
 * - number -> Number
 * - boolean -> true/false
 * - text -> string
 * - les champs vides sont ignorés
 */
function prepareSpecsForSave(productType, rawSpecs = {}) {
  const config = SPEC_CONFIG[productType] || {};
  const prepared = {};

  config.forEach((field) => {
    const value = rawSpecs[field.name];

    if (field.type === "boolean") {
      prepared[field.name] = !!value;
      return;
    }

    if (value === undefined || value === null || value === "") {
      return;
    }

    if (field.type === "number") {
      const numberValue = Number(value);

      if (Number.isFinite(numberValue)) {
        prepared[field.name] = numberValue;
      }

      return;
    }

    prepared[field.name] = String(value).trim();
  });

  return prepared;
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);
  const [specs, setSpecs] = useState({});

  const [selectedFiles, setSelectedFiles] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [editingId, setEditingId] = useState(null);


  /**
   * Charger les produits en temps réel
   */
  useEffect(() => {
    const q = query(
      collection(db, "products_prosoft"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setProducts(list);
      },
      (error) => {
        console.error("Erreur chargement produits :", error);
      }
    );

    return () => unsub();
  }, []);

  /**
   * Configuration technique correspondant au type actuel
   */
  const currentSpecConfig = useMemo(
    () => SPEC_CONFIG[form.type] || [],
    [form.type]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSpecChange = (e) => {
    const { name, value, type, checked } = e.target;

    setSpecs((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCategoryChange = (e) => {
    const [categorySlug, type] = e.target.value.split("|");

    setForm((prev) => ({
      ...prev,
      categorySlug,
      type,
    }));

    // Reset specs quand on change de type
    setSpecs({});
  };

  const handleFilesChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSpecs({});
    setSelectedFiles([]);
    setEditingId(null);
    setProgress(0);
    setUploading(false);
  };

  /**
   * Upload des images sur Firebase Storage
   */
  const uploadImagesAndGetUrls = async () => {
    if (!selectedFiles.length) {
      return {
        images: [],
        mainImage: "",
      };
    }

    const urls = [];

    for (const file of selectedFiles) {
      const storageRef = ref(
        storage,
        `products_prosoft/${Date.now()}-${file.name.replace(/\s+/g, "_")}`
      );

      const uploadTask = uploadBytesResumable(storageRef, file);

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",

          (snap) => {
            const pct =
              (snap.bytesTransferred / snap.totalBytes) * 100;

            setProgress(Math.round(pct));
          },

          (error) => {
            console.error("🔥 Erreur upload Firebase Storage :", {
              code: error.code,
              message: error.message,
              serverResponse: error?.customData?.serverResponse,
            });

            if (
              error.code === "storage/unauthorized" ||
              error.code === "storage/unauthenticated"
            ) {
              alert(
                "Vous n'êtes pas autorisé à téléverser des images.\n\n" +
                  "- Vérifiez que vous êtes bien connecté (Firebase Auth)\n" +
                  "- Et que vos règles Storage autorisent l'écriture pour les utilisateurs connectés."
              );
            } else {
              alert(
                "Erreur lors de l'upload de l'image.\n\nCode: " +
                  error.code +
                  "\nMessage: " +
                  error.message
              );
            }

            reject(error);
          },

          async () => {
            const url = await getDownloadURL(
              uploadTask.snapshot.ref
            );

            urls.push(url);

            resolve();
          }
        );
      });
    }

    return {
      images: urls,
      mainImage: urls[0] || "",
    };
  };

  /**
   * Création / mise à jour produit
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);

      const slug = form.slug || slugify(form.name);

      const { images, mainImage } =
        await uploadImagesAndGetUrls();

      // Nettoyage et conversion des spécifications avant Firestore
      const preparedSpecs = prepareSpecsForSave(
        form.type,
        specs
      );

      const baseData = {
        ...form,

        price: Number(form.price) || 0,

        oldPrice: form.oldPrice
          ? Number(form.oldPrice)
          : null,

        stockQty: form.stockQty
          ? Number(form.stockQty)
          : 0,

        slug,

        specs: preparedSpecs,

        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        const docRef = doc(
          db,
          "products_prosoft",
          editingId
        );

        const updatePayload = {
          ...baseData,
        };

        /**
         * Si aucune nouvelle image n'est sélectionnée,
         * on conserve les images existantes.
         */
        if (images.length) {
          updatePayload.images = images;
          updatePayload.mainImage = mainImage;
        }

        await updateDoc(
          docRef,
          updatePayload
        );

        console.log("Produit mis à jour :", editingId);
      } else {
        const docRef = await addDoc(
          collection(db, "products_prosoft"),
          {
            ...baseData,

            images,
            mainImage,

            createdAt: serverTimestamp(),
          }
        );

        console.log("Produit créé:", docRef.id);
      }

      resetForm();
    } catch (err) {
      console.error("Erreur handleSubmit:", err);

      alert(
        "Erreur lors de l'enregistrement du produit."
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Édition d'un produit
   */
  const handleEdit = (product) => {
    setEditingId(product.id);

    setForm({
      name: product.name || "",
      slug: product.slug || "",
      brand: product.brand || "",
      categorySlug: product.categorySlug || "",
      type: product.type || "",
      price: product.price ?? "",
      oldPrice: product.oldPrice ?? "",
      inStock: product.inStock ?? true,
      stockQty: product.stockQty ?? "",
      shortDescription: product.shortDescription || "",
      description: product.description || "",
      isFeatured: product.isFeatured ?? false,
    });

    setSpecs(product.specs || {});

    setSelectedFiles([]);

    setProgress(0);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /**
   * Suppression
   */
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) {
      return;
    }

    try {
      await deleteDoc(
        doc(db, "products_prosoft", id)
      );
    } catch (err) {
      console.error(err);

      alert(
        "Erreur lors de la suppression."
      );
    }
  };

  return (
    <div className="container py-4">
      {/* ========================= */}
      {/* TITRE */}
      {/* ========================= */}

      <div className="mb-3">
        <h4 className="fw-bold mb-1">Admin — Produits</h4>
        <div className="small text-muted">
          Gestion du catalogue ProSoft Tools
        </div>
      </div>

      <div className="row g-4">
        {/* ================================================= */}
        {/* FORMULAIRE */}
        {/* ================================================= */}

        <div className="col-lg-5">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                {editingId
                  ? "Modifier le produit"
                  : "Ajouter un produit"}
              </h6>

              <form
                onSubmit={handleSubmit}
                className="small"
              >
                {/* Nom */}

                <div className="mb-2">
                  <label className="form-label">
                    Nom du produit
                  </label>

                  <input
                    type="text"
                    name="name"
                    className="form-control form-control-sm"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Slug */}

                <div className="mb-2">
                  <label className="form-label">
                    Slug (URL){" "}
                    <span className="text-muted">
                      (auto si vide)
                    </span>
                  </label>

                  <input
                    type="text"
                    name="slug"
                    className="form-control form-control-sm"
                    value={form.slug}
                    onChange={handleChange}
                  />
                </div>

                {/* Catégorie */}

                <div className="mb-2">
                  <label className="form-label">
                    Catégorie
                  </label>

                  <select
                    className="form-select form-select-sm"
                    value={`${form.categorySlug}|${form.type}`}
                    onChange={handleCategoryChange}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option
                        key={opt.type}
                        value={`${opt.categorySlug}|${opt.type}`}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Marque */}

                <div className="mb-2">
                  <label className="form-label">
                    Marque
                  </label>

                  <input
                    type="text"
                    name="brand"
                    className="form-control form-control-sm"
                    value={form.brand}
                    onChange={handleChange}
                  />
                </div>

                {/* Prix */}

                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label">
                      Prix (CFA)
                    </label>

                    <input
                      type="number"
                      name="price"
                      className="form-control form-control-sm"
                      value={form.price}
                      onChange={handleChange}
                      min="0"
                      required
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label">
                      Ancien prix
                      <span className="text-muted">
                        {" "}
                        (promo)
                      </span>
                    </label>

                    <input
                      type="number"
                      name="oldPrice"
                      className="form-control form-control-sm"
                      value={form.oldPrice}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                </div>

                {/* Stock */}

                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label">
                      Qté en stock
                    </label>

                    <input
                      type="number"
                      name="stockQty"
                      className="form-control form-control-sm"
                      value={form.stockQty}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>

                  <div className="col-6 d-flex align-items-end">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="inStockChk"
                        name="inStock"
                        checked={form.inStock}
                        onChange={handleChange}
                      />

                      <label
                        className="form-check-label"
                        htmlFor="inStockChk"
                      >
                        En stock
                      </label>
                    </div>
                  </div>
                </div>

                {/* Description courte */}

                <div className="mb-2">
                  <label className="form-label">
                    Description courte (listing)
                  </label>

                  <textarea
                    name="shortDescription"
                    className="form-control form-control-sm"
                    rows={2}
                    value={form.shortDescription}
                    onChange={handleChange}
                  />
                </div>

                {/* Description détaillée */}

                <div className="mb-2">
                  <label className="form-label">
                    Description détaillée
                  </label>

                  <textarea
                    name="description"
                    className="form-control form-control-sm"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                  />
                </div>

                {/* Featured */}

                <div className="mb-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="featuredChk"
                      name="isFeatured"
                      checked={form.isFeatured}
                      onChange={handleChange}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="featuredChk"
                    >
                      Mettre en avant
                      (Meilleures offres,
                      homepage…)
                    </label>
                  </div>
                </div>

                {/* ================================================= */}
                {/* SPECS */}
                {/* ================================================= */}

                <div className="mb-2 border-top pt-3">
                  <div className="fw-semibold mb-2">
                    Caractéristiques techniques
                  </div>

                  {form.type === "paper-roll" && (
                    <div className="alert alert-light border py-2 small">
                      La longueur des rouleaux est
                      maintenant enregistrée en{" "}
                      <strong>millimètres (mm)</strong>.
                    </div>
                  )}

                  {currentSpecConfig.length === 0 && (
                    <p className="small text-muted">
                      Aucune configuration de
                      spécification pour ce type.
                    </p>
                  )}

                  {currentSpecConfig.map((field) => {
                    if (field.type === "boolean") {
                      return (
                        <div
                          className="form-check mb-2"
                          key={field.name}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`spec-${field.name}`}
                            name={field.name}
                            checked={
                              !!specs[field.name]
                            }
                            onChange={
                              handleSpecChange
                            }
                          />

                          <label
                            className="form-check-label"
                            htmlFor={`spec-${field.name}`}
                          >
                            {field.label}
                          </label>
                        </div>
                      );
                    }

                    return (
                      <div
                        className="mb-2"
                        key={field.name}
                      >
                        <label className="form-label small mb-1">
                          {field.label}
                        </label>

                        <input
                          type={
                            field.type ===
                            "number"
                              ? "number"
                              : "text"
                          }
                          name={field.name}
                          className="form-control form-control-sm"
                          value={
                            specs[field.name] ??
                            ""
                          }
                          onChange={
                            handleSpecChange
                          }
                          min={
                            field.type ===
                            "number"
                              ? "0"
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </div>

                {/* ================================================= */}
                {/* IMAGES */}
                {/* ================================================= */}

                <div className="mb-2 border-top pt-3">
                  <label className="form-label">
                    Images produit
                  </label>

                  <input
                    type="file"
                    className="form-control form-control-sm"
                    multiple
                    accept="image/*"
                    onChange={handleFilesChange}
                  />

                  {uploading && (
                    <div className="small text-muted mt-1">
                      Upload… {progress}%
                    </div>
                  )}

                  {selectedFiles.length > 0 && (
                    <div className="small text-muted mt-1">
                      {selectedFiles.length} fichier(s)
                      sélectionné(s).
                    </div>
                  )}

                  {editingId &&
                    !selectedFiles.length && (
                      <div className="small text-muted mt-1">
                        Si tu ne sélectionnes rien,
                        les images actuelles sont
                        conservées.
                      </div>
                    )}
                </div>

                {/* ================================================= */}
                {/* BOUTONS */}
                {/* ================================================= */}

                <div className="d-flex gap-2 mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={uploading}
                  >
                    {uploading
                      ? "Enregistrement..."
                      : editingId
                      ? "Mettre à jour"
                      : "Ajouter le produit"}
                  </button>

                  {editingId && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={resetForm}
                      disabled={uploading}
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* LISTE PRODUITS */}
        {/* ================================================= */}

        <div className="col-lg-7">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  Produits enregistrés
                </h6>

                <span className="badge bg-light text-dark border">
                  {products.length}
                </span>
              </div>

              <div className="table-responsive small">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Catégorie</th>
                      <th>Prix</th>
                      <th>Stock</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {products.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center text-muted py-4"
                        >
                          Aucun produit pour le
                          moment.
                        </td>
                      </tr>
                    )}

                    {products.map((p) => {
                      return (
                        <tr key={p.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {p.mainImage && (
                                <img
                                  src={
                                    p.mainImage
                                  }
                                  alt={p.name}
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit:
                                      "cover",
                                    borderRadius:
                                      6,
                                  }}
                                />
                              )}

                              <div>
                                <div className="fw-semibold">
                                  {p.name}
                                </div>

                                <div className="text-muted small">
                                  {p.brand}
                                  {p.brand
                                    ? " — "
                                    : ""}
                                  {p.slug}
                                </div>

                              </div>
                            </div>
                          </td>

                          <td className="small">
                            {
                              p.categorySlug
                            }

                            <br />

                            <span className="text-muted">
                              {p.type}
                            </span>
                          </td>

                          <td>
                            {Number(
                              p.price || 0
                            ).toLocaleString(
                              "fr-FR"
                            )}{" "}
                            CFA
                          </td>

                          <td>
                            {p.inStock ? (
                              <span className="badge bg-success-subtle text-success">
                                {p.stockQty ||
                                  "OK"}
                              </span>
                            ) : (
                              <span className="badge bg-danger-subtle text-danger">
                                Rupture
                              </span>
                            )}
                          </td>

                          <td className="text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm me-1"
                              onClick={() =>
                                handleEdit(
                                  p
                                )
                              }
                            >
                              Éditer
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() =>
                                handleDelete(
                                  p.id
                                )
                              }
                            >
                              Suppr.
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
